import { ApolloLink, Observable, type FetchResult, type Operation } from "@apollo/client/core";
import { getMainDefinition } from "@apollo/client/utilities";
import { print } from "graphql";

/**
 * Apollo Link for AWS AppSync real-time subscriptions.
 *
 * AppSync uses its own WebSocket subprotocol (not the standard graphql-ws
 * transport), so this implements that protocol directly against a raw
 * WebSocket — no external dependency. Queries and mutations do NOT go through
 * here; they stay on the existing HTTP link (see apollo.tsx `split`).
 *
 * Protocol summary (Lambda/custom auth):
 *   1. Connect to wss://<id>.appsync-realtime-api.<region>.amazonaws.com/graphql
 *      with subprotocol "graphql-ws" and ?header=<b64>&payload=<b64>, where the
 *      header carries { host, Authorization: <token> }.
 *   2. Send { type: "connection_init" }; await { type: "connection_ack" }.
 *   3. Per subscription: send { id, type: "start", payload: { data, extensions } }.
 *   4. Receive { type: "data", id, payload }, plus { type: "ka" } keep-alive pings.
 *   5. Send { id, type: "stop" } on unsubscribe.
 *
 * NOTE: the protocol details (auth header field, message shapes) must be
 * validated against the deployed AppSync API; this is the one piece that can't
 * be exercised without a live endpoint.
 */

const WS_SUBPROTOCOL = "graphql-ws";

/**
 * `crypto.randomUUID()` exists natively in browsers and Node, but not in React
 * Native's Hermes runtime — this package runs in both, so fall back to a
 * manual UUID v4 when the global isn't present rather than requiring every
 * consumer to install a polyfill.
 */
export const randomUUID = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/** True for subscription operations (which must route to AppSync). */
export const isSubscriptionOperation = (operation: Operation): boolean => {
    const def = getMainDefinition(operation.query);
    return def.kind === "OperationDefinition" && def.operation === "subscription";
};

/** Derive the AppSync realtime (WebSocket) endpoint from its HTTP GraphQL URL. */
export const deriveRealtimeUrl = (httpUrl: string): string =>
    httpUrl.replace(/^https:\/\//, "wss://").replace("appsync-api", "appsync-realtime-api");

/** Auth header AppSync expects for a Lambda-authorized connection / operation. */
export const buildAuthHeader = (httpHost: string, token: string): Record<string, string> => ({
    host: httpHost,
    Authorization: token,
});

const toBase64 = (value: string): string => btoa(value);

/** Build the realtime connection URL with the base64 header/payload query params. */
export const buildConnectionUrl = (realtimeUrl: string, authHeader: Record<string, string>): string => {
    const params = new URLSearchParams({
        header: toBase64(JSON.stringify(authHeader)),
        payload: toBase64(JSON.stringify({})),
    });
    return `${realtimeUrl}?${params.toString()}`;
};

interface ServerMessage {
    type: string;
    id?: string;
    payload?: { data?: unknown; errors?: unknown };
}

/**
 * Manages a single shared AppSync WebSocket connection and multiplexes
 * subscriptions over it. One instance is created per Apollo client.
 */
class AppSyncConnection {
    private socket?: WebSocket;
    private connecting?: Promise<WebSocket>;
    private readonly observers = new Map<string, ZenObserver>();

    constructor(
        private readonly realtimeUrl: string,
        private readonly httpHost: string,
        private readonly getToken: () => Promise<string>,
    ) {}

    private dispatch(msg: ServerMessage): void {
        if (msg.type === "ka") return; // keep-alive
        if (!msg.id) return;
        const observer = this.observers.get(msg.id);
        if (!observer) return;
        if (msg.type === "data" && msg.payload) {
            observer.next({ data: msg.payload.data, errors: msg.payload.errors } as FetchResult);
        } else if (msg.type === "error") {
            observer.error(new Error(`AppSync subscription error: ${JSON.stringify(msg.payload)}`));
        } else if (msg.type === "complete") {
            observer.complete();
            this.observers.delete(msg.id);
        }
    }

    private async ensureConnected(): Promise<WebSocket> {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) return this.socket;
        if (this.connecting) return this.connecting;

        this.connecting = (async () => {
            const token = await this.getToken();
            const url = buildConnectionUrl(this.realtimeUrl, buildAuthHeader(this.httpHost, token));
            const socket = new WebSocket(url, WS_SUBPROTOCOL);

            await new Promise<void>((resolve, reject) => {
                socket.onopen = () => socket.send(JSON.stringify({ type: "connection_init" }));
                socket.onerror = () => reject(new Error("AppSync WebSocket error"));
                socket.onclose = () => {
                    this.socket = undefined;
                    this.connecting = undefined;
                    for (const obs of this.observers.values()) obs.error(new Error("AppSync connection closed"));
                    this.observers.clear();
                };
                socket.onmessage = (event: MessageEvent) => {
                    const msg = JSON.parse(event.data as string) as ServerMessage;
                    if (msg.type === "connection_ack") resolve();
                    else if (msg.type === "connection_error") reject(new Error("AppSync connection_error"));
                    else this.dispatch(msg);
                };
            });

            this.socket = socket;
            return socket;
        })();

        return this.connecting;
    }

    request(operation: Operation): Observable<FetchResult> {
        return new Observable<FetchResult>((observer) => {
            const id = randomUUID();
            let active = true;

            void (async () => {
                try {
                    const socket = await this.ensureConnected();
                    if (!active) return;
                    this.observers.set(id, observer);
                    const token = await this.getToken();
                    socket.send(
                        JSON.stringify({
                            id,
                            type: "start",
                            payload: {
                                data: JSON.stringify({
                                    query: print(operation.query),
                                    variables: operation.variables,
                                }),
                                extensions: { authorization: buildAuthHeader(this.httpHost, token) },
                            },
                        }),
                    );
                } catch (err) {
                    observer.error(err as Error);
                }
            })();

            return () => {
                active = false;
                this.observers.delete(id);
                if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                    this.socket.send(JSON.stringify({ id, type: "stop" }));
                }
            };
        });
    }
}

interface ZenObserver {
    next: (value: FetchResult) => void;
    error: (err: Error) => void;
    complete: () => void;
}

export interface AppSyncLinkConfig {
    /** AppSync HTTP GraphQL URL (VITE_APPSYNC_GRAPHQL_URL). */
    httpUrl: string;
    /** Returns the current Auth0 access token. */
    getToken: () => Promise<string>;
}

/**
 * Builds the AppSync subscription ApolloLink, or returns null when no AppSync
 * endpoint is configured (e.g. local dev) — callers then skip the subscription
 * split entirely.
 */
export const buildAppSyncSubscriptionLink = (config: AppSyncLinkConfig | undefined): ApolloLink | null => {
    if (!config?.httpUrl) return null;
    const realtimeUrl = deriveRealtimeUrl(config.httpUrl);
    const httpHost = new URL(config.httpUrl).host;
    const connection = new AppSyncConnection(realtimeUrl, httpHost, config.getToken);
    return new ApolloLink((operation) => connection.request(operation));
};
